import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    useFieldArray,
} from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_SURV_QUES_INFO,
    SG_SURV_QUES_LANG_LIST,
    SF_SURV_ITM_INFO,
    SG_SURV_ITM_LANG_LIST,
} from "./services/PtliSurvQuesMgmtService";
import { SG_SURV_MGMT_LIST } from "../qustnr/services/PtliSurvMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0418005Q } from "../qustnr/PTLI0418005Q";
import { PTLI0419005Q } from "./PTLI0419005Q";

/*
 * Survey Question Registration
 * !== 설문 질문 등록 ==!
 */

export const PTLI0419002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survQuesInfo: useForm({
            defaultSchema: SF_SURV_QUES_INFO,
            defaultValues: {
                quesTpCd: "1",
                maxSeltCnt: "1",
            },
        }),
        survItmInfo: useForm({
            defaultSchema: SF_SURV_ITM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survMgmtList: useGrid({
            defaultSchema: SG_SURV_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        survQuesLangList: useGrid({
            defaultSchema: SG_SURV_QUES_LANG_LIST,
        }),
        survItmLangList: useGrid({
            defaultSchema: SG_SURV_ITM_LANG_LIST,
        }),
    };

    const { fields: itemFields, remove, append, prepend, insert } = useFieldArray(form.survItmInfo.schema.items);

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        createSurvQues: useFetch({
            api: (payload) => APIS.createSurvQues(payload),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli0419001q);
                    },
                    onCancel: () => {
                        navigate(URLS.ptli0419001q);
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.survQuesInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.ptl.00837", // An error occurred during processing., !== 처리중 문제가 발생하였습니다. ==!
                });
            },
        }),

        getNextQuesOrd: useFetch({
            api: ({ qestnrId }: { qestnrId: string }) => APIS.getNextQuesOrd(qestnrId),
            onSuccess: (nextOrd: number) => {
                form.survQuesInfo.setValue("quesOrd", String(nextOrd));
            },
            onError: () => {
                toast.showToast({ type: "warning", content: "msg.00006" }); // An error occurred during processing., !== 처리중 문제가 발생하였습니다. ==!
            },
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        createSurv: () => {
            if (!comnUtils.isEmpty(form.survQuesInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.survQuesInfo.handleSubmit(
                () => {
                    // QUES 파트
                    grid.survQuesLangList.clearData();

                    langCodes.forEach((langCd) => {
                        const quesCn = form.survQuesInfo.getValue(`quesCn${langCd}`); // quesCnEN/TZ/KO
                        if (!comnUtils.isEmpty(quesCn)) {
                            grid.survQuesLangList.addRow({
                                langCd,
                                quesCn,
                            });
                        }
                    });
                    if (grid.survQuesLangList.getData().length === 0) {
                        modal.openModal({ content: t("msg.ptl.00682", { 0: t("T_QUESTIONS") + " TAB" }) }); // Please check the required entries for {0} !== {0}의 필수입력 항목을 확인하시기 바랍니다. ==!
                        return;
                    }

                    const quesInfo = form.survQuesInfo.getValues();
                    const survQuesLangList = grid.survQuesLangList.getData();

                    let payload: any = { ...quesInfo, survQuesLangList };

                    // ITM 파트
                    if (quesInfo?.quesTpCd === "1") {
                        if (!comnUtils.isEmpty(form.survItmInfo.errors)) {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            return;
                        }
                        form.survItmInfo.handleSubmit(
                            () => {
                                const survItmInfo = form.survItmInfo.getValues("items");

                                if (survItmInfo.length === 0) {
                                    modal.openModal({ content: "msg.ptl.00838" }); // An objective survey must contain at least one question item. !== 객관식은 하나의 항목 이상을 가져야 합니다. ==!
                                    return;
                                }

                                const itmGroupedList = survItmInfo
                                    .map((row: any) => ({
                                        itmOrd: String(row.itmOrd),
                                        itmCn: String(row.itmCnEN),
                                        survItmLangList: langCodes
                                            .map((langCd) => ({
                                                langCd,
                                                itmCn: row[`itmCn${langCd}`],
                                            }))
                                            .filter((x) => !comnUtils.isEmpty(x.itmCn)),
                                    }))
                                    .filter((g: any) => g.survItmLangList.length > 0);

                                if (itmGroupedList.length === 0) {
                                    modal.openModal({ content: t("msg.ptl.00682", { 0: t("T_ITEMS") }) }); // Please check the required entries for {0} !== {0}의 필수입력 항목을 확인하시기 바랍니다. ==!
                                    return;
                                }

                                payload.survItmInfo = itmGroupedList;

                                modal.openModal({
                                    content: "msg.00101", // Do you want to save ? !==저장 하시겠습니까? ==!
                                    onConfirm: () => fetch.createSurvQues.fetch(payload),
                                    close: false,
                                });
                            },
                            () => {
                                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            },
                        )();
                    } else {
                        modal.openModal({
                            content: "msg.00101", // Do you want to save ? !==저장 하시겠습니까? ==!
                            onConfirm: () => fetch.createSurvQues.fetch(payload),
                            close: false,
                        });
                    }
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    const currQuesTpCd = form.survQuesInfo.watch("quesTpCd");

    useEffect(() => {}, []);

    const renumberItmOrd = () => {
        const items = form.survItmInfo.getValue("items") || [];
        items.forEach((item: any, index: any) => {
            form.survItmInfo.setValue(`items.${index}.itmOrd`, String(index + 1));
        });
    };

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_QUESTIONS")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control
                                    {...form.survQuesInfo.schema.qustnrTtle}
                                    readOnly={true}
                                    rightButton={{
                                        icon: "search",
                                        onClick: () => {
                                            const modalCallback = async (item: any) => {
                                                if (!comnUtils.isEmpty(item)) {
                                                    form.survQuesInfo.setValue("qustnrTtle", item.qustnrTtle);
                                                    form.survQuesInfo.setValue("qestnrId", item.qestnrId);

                                                    try {
                                                        const { data } = await APIS.getNextQuesOrd(item.qestnrId); // ← axios 응답
                                                        form.survQuesInfo.setValue("quesOrd", String(data.nextOrd)); // ← 문자열로
                                                    } catch (e) {
                                                        toast.showToast({ type: "warning", content: "msg.00006" });
                                                    }
                                                }
                                                modal.closeModal();
                                            };

                                            modal.openModal({
                                                draggable: true,
                                                size: "md",
                                                content: <PTLI0418005Q callback={modalCallback} />,
                                            });
                                        },
                                    }}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesOrd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesCnEN}></Group.Control>
                                <Group.Control {...form.survQuesInfo.schema.quesCnTZ}></Group.Control>
                                <Group.Control {...form.survQuesInfo.schema.quesCnKO}></Group.Control>
                            </Group.Row>

                            {currQuesTpCd === "1" && (
                                <Group.Row>
                                    <Group.Control {...form.survQuesInfo.schema.maxSeltCnt}></Group.Control>
                                </Group.Row>
                            )}
                        </Group.Section>
                        <Group.Section hidden={true}>
                            {" "}
                            <Grid {...grid.survQuesLangList.grid} />
                        </Group.Section>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_ITEMS")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            {/* 항목 추가 버튼 */}
                            <Layout direction="row">
                                <Layout.Right>
                                    {currQuesTpCd === "1" && (
                                        <Button
                                            onClick={() => {
                                                // 새 항목 append (순번은 배열 길이+1 로 기본 세팅)
                                                append({
                                                    itmOrd: String((itemFields?.length ?? 0) + 1),
                                                    itmCnEN: "",
                                                    itmCnTZ: "",
                                                    itmCnKO: "",
                                                });
                                            }}
                                        >
                                            {t("B_QUES_ITM_ADD")}
                                        </Button>
                                    )}
                                </Layout.Right>
                            </Layout>
                        </Group.Section>

                        {/* 동적 항목 렌더링 */}
                        {itemFields.map((itmInfo, i) => {
                            itmInfo.schema.itmOrd.readOnly = true;
                            return (
                                <Group.Section key={itmInfo.key}>
                                    <Layout direction="row">
                                        <Layout.Right>
                                            <Button
                                                role="gridDelete"
                                                onClick={() => {
                                                    remove(i);
                                                    setTimeout(renumberItmOrd, 0);
                                                }}
                                            >
                                                {t("B_DELETE")}
                                            </Button>
                                        </Layout.Right>
                                    </Layout>
                                    <Group.Row>
                                        <Group.Control {...itmInfo.schema.itmOrd} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...itmInfo.schema.itmCnEN} />
                                        <Group.Control {...itmInfo.schema.itmCnTZ} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...itmInfo.schema.itmCnKO} />
                                    </Group.Row>
                                </Group.Section>
                            );
                        })}

                        <Group.Section hidden={true}>
                            <Grid {...grid.survItmLangList.grid} />
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0419001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.createSurv();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
